import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useUnmount } from 'ahooks';
import { Toast } from 'antd-mobile/2x';
import { Loading3QuartersOutlined } from '@ant-design/icons';
import { ToastProps } from 'antd-mobile/es/components/toast/toast';
import Icon from 'common-package/src/components/Icon';
import { Icon as ContextIcon } from '@fuxi/eevee-icon';
import cx from './index.module.scss';

interface ToastContentProps {
  icon: 'success' | 'cross' | 'fail' | 'loading' | 'clock' | 'none';
  content: React.ReactNode;
  color?: string;
}

type MyToastProps = Omit<ToastProps, 'content'> & { color?: string };

const genToastExtraContent = ({ icon, content, color }: ToastContentProps) => ({
  position: 'bottom' as ToastProps['position'],
  content: (
    <div className="custom-adm-content-wrapper">
      {!!icon && icon !== 'none' && (
        <div className="custom-adm-content-icon">
          {icon === 'loading' ? (
            <Loading3QuartersOutlined spin style={{ fontSize: '22px' }} />
          ) : (
            <Icon name={icon} size={24} color={color} />
          )}
        </div>
      )}
      <div className="custom-adm-content-text">{content}</div>
    </div>
  ),
});

class MyToast {
  success(content: React.ReactNode, config?: MyToastProps) {
    Toast.show({
      ...genToastExtraContent({ icon: 'success', content, color: config?.color }),
      ...(config ? config : {}),
    });
  }
  warn(content: React.ReactNode, config?: MyToastProps) {
    Toast.show({
      ...genToastExtraContent({ icon: 'fail', content, color: config?.color }),
      ...(config ? config : {}),
    });
  }
  fail(content: React.ReactNode, config?: MyToastProps) {
    Toast.show({
      ...genToastExtraContent({ icon: 'cross', content, color: config?.color }),
      ...(config ? config : {}),
    });
  }
  clock(content: React.ReactNode, config?: MyToastProps) {
    Toast.show({
      ...genToastExtraContent({ icon: 'clock', content, color: config?.color }),
      ...(config ? config : {}),
    });
  }
  loading(content?: React.ReactNode, config?: MyToastProps) {
    Toast.show({
      ...genToastExtraContent({ icon: 'loading', content, color: config?.color }),
      ...(config ? config : {}),
    });
  }
  show(content?: React.ReactNode, config?: MyToastProps) {
    Toast.show({
      ...genToastExtraContent({ icon: 'none', content, color: config?.color }),
      ...(config ? config : {}),
    });
  }

  /** 竖屏toast */
  showVScreen(content?: React.ReactNode, config?: MyToastProps) {
    Toast.show({
      ...genToastExtraContent({ icon: 'none', content, color: config?.color }),
      ...(config ? config : {}),
      maskClassName: 'v-screen-toast',
    });
  }

  warnVScreen(content: React.ReactNode, config?: MyToastProps) {
    Toast.show({
      ...genToastExtraContent({ icon: 'fail', content, color: config?.color }),
      ...(config ? config : {}),
      maskClassName: 'v-screen-toast',
    });
  }

  loadingVScreen(content?: React.ReactNode, config?: MyToastProps) {
    Toast.show({
      ...genToastExtraContent({ icon: 'loading', content, color: config?.color }),
      ...(config ? config : {}),
      maskClassName: 'v-screen-toast',
    });
  }

  clear() {
    Toast.clear();
  }
}

export interface CustomToastRefAttrs {
  fail: (content: string) => void;
}

export const CustomToast = forwardRef<CustomToastRefAttrs>((_, ref) => {
  const [content, setContent] = useState('');
  const [open, setOpen] = useState(false);
  const [controlShow, setControlShow] = useState(false);
  const timerRef = useRef(null);

  useImperativeHandle(ref, () => ({
    fail: content => {
      clearTimeout(timerRef.current);
      setOpen(true);
      setContent(content);
      timerRef.current = setTimeout(() => setOpen(false), 2000);
    },
  }));

  useUnmount(() => {
    clearTimeout(timerRef.current);
  });

  useEffect(() => {
    let timer = null;
    if (open) {
      setControlShow(true);
    } else {
      timer = setTimeout(() => setControlShow(false), 300);
    }
    return () => {
      clearTimeout(timer);
      timer = null;
    };
  }, [open]);

  return (
    <div className={cx('custom-toast', { open, show: controlShow })}>
      <ContextIcon name="warning" className={cx('icon')} />
      <div className={cx('message-text')}>{content}</div>
    </div>
  );
});

export default new MyToast();
